// src/hooks/useReaderSignals.js
// React bindings for the reader-signal rail (src/services/readerSignals.js).
//
// usePositionAggregate: where readers land at one fork — but only once THIS
// reader has committed (the aggregate never leads the witness, same rule as
// the lens lines). Mount also reconciles: if the device's local choice was
// never successfully counted (offline, or the write failed), it is synced
// now, so the rail heals itself as forks come back on screen.
import { useState, useEffect } from 'react';
import {
  fetchPositionAggregate, syncPositionSignal, SIGNALS_EVENT,
  fetchDiscussionDemand, toggleDiscussionDemand, discussionDemandRaised,
} from '../services/readerSignals';

/**
 * @param {string} pointId    the fork's id
 * @param {string} committedChoice  this reader's current choice, or null
 * @returns {{counts: Object, total: number} | null}  null until committed
 *   and loaded; also null when the rail is unreachable (UI shows nothing).
 */
export const usePositionAggregate = (pointId, committedChoice) => {
  const [aggregate, setAggregate] = useState(null);

  useEffect(() => {
    if (!committedChoice) {
      setAggregate(null);
      return undefined;
    }
    let alive = true;
    const load = async () => {
      // Count this device's choice first (no-op when already counted), so
      // the numbers the reader sees always include their own voice.
      await syncPositionSignal(pointId, committedChoice);
      const data = await fetchPositionAggregate(pointId);
      if (alive) setAggregate(data);
    };
    load();
    const refresh = () => { fetchPositionAggregate(pointId).then((d) => { if (alive) setAggregate(d); }); };
    window.addEventListener(SIGNALS_EVENT, refresh);
    return () => {
      alive = false;
      window.removeEventListener(SIGNALS_EVENT, refresh);
    };
  }, [pointId, committedChoice]);

  return aggregate;
};

/**
 * The live-discussion demand probe for one module: the count of raised
 * hands (revealed only after this reader raises theirs), whether this
 * device's hand is up, and the toggle.
 */
export const useDiscussionDemand = (moduleId) => {
  const [raised, setRaised] = useState(() => discussionDemandRaised(moduleId));
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRaised(discussionDemandRaised(moduleId));
    setCount(null);
    if (!discussionDemandRaised(moduleId)) return undefined;
    let alive = true;
    fetchDiscussionDemand(moduleId).then((n) => { if (alive) setCount(n); });
    return () => { alive = false; };
  }, [moduleId]);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const next = await toggleDiscussionDemand(moduleId);
      if (next === null) return; // fail soft — nothing changes on screen
      setRaised(next);
      setCount(next ? await fetchDiscussionDemand(moduleId) : null);
    } finally {
      setBusy(false);
    }
  };

  return { raised, count, busy, toggle };
};
